import { createPanGesture, type PanAxis, type PanGesture, type PanGestureEndDetail } from '@vielzeug/gesture';
import {
  createStableId,
  define,
  getHost,
  html,
  onCleanup,
  onEvent,
  onMounted,
  prop,
  ref,
  useEmit,
  useSlots,
} from '@vielzeug/ore';
import { signal } from '@vielzeug/ripple';

import type { OverlayOpenChangeDetail } from '../../core';
import '../../content/icon/icon';
import { coarsePointerMixin, forcedColorsMixin, reducedMotionMixin } from '../../styles';
import { useDialogControl } from '../shared/use-dialog';
import styles from './drawer.css?inline';

type DrawerPlacement = 'left' | 'right' | 'top' | 'bottom';
type DrawerSize = 'sm' | 'lg' | 'full';
type DrawerBackdrop = 'opaque' | 'blur' | 'transparent';
type DrawerDragHandlePlacement = 'outside' | 'inset';
type DrawerSwipeConfig = {
  axis: PanAxis;
  closingDistance: (distance: number) => number;
  translate: (distance: number) => string;
};

const parseOptionalBool = (value: string | null): boolean | undefined =>
  value == null ? undefined : value === '' || value === 'true';

const drawerSwipeConfig: Record<DrawerPlacement, DrawerSwipeConfig> = {
  bottom: {
    axis: 'y',
    closingDistance: (distance) => Math.max(0, distance),
    translate: (distance) => `translateY(${distance}px)`,
  },
  left: {
    axis: 'x',
    closingDistance: (distance) => Math.max(0, -distance),
    translate: (distance) => `translateX(${distance}px)`,
  },
  right: {
    axis: 'x',
    closingDistance: (distance) => Math.max(0, distance),
    translate: (distance) => `translateX(${distance}px)`,
  },
  top: {
    axis: 'y',
    closingDistance: (distance) => Math.max(0, -distance),
    translate: (distance) => `translateY(${distance}px)`,
  },
};

/** Element interface exposing the imperative API for `ore-drawer`. */
export interface DrawerElement extends HTMLElement, Omit<OreDrawerProps, 'title'> {
  /** Programmatically close the drawer with the exit animation. */
  hide(): void;
  /** Programmatically open the drawer. Equivalent to setting `open`. */
  show(): void;
}

/** Drawer component properties */

export type OreDrawerEvents = {
  'open-change': OverlayOpenChangeDetail;
};

export type OreDrawerProps = {
  /** Backdrop style — 'opaque' (default), 'blur', or 'transparent' */
  backdrop?: DrawerBackdrop;
  /** Initial uncontrolled open state. Ignored when `open` is set. */
  'default-open'?: boolean;
  /** Show the close (×) button in the header (default: true) */
  dismissible?: boolean;
  /** Drag handle position used for swipe-to-close gestures */
  'drag-handle-placement'?: DrawerDragHandlePlacement;
  /**
   * CSS selector for the element inside the drawer that should receive focus on open.
   * Defaults to native dialog focus management (first focusable element).
   * @example '#submit-btn' | 'input[name="email"]'
   */
  'initial-focus'?: string;
  /**
   * Invisible accessible label for the dialog (`aria-label`).
   * Use when the drawer has no visible title (e.g. image-only content).
   * When omitted, `aria-labelledby` points to the visible header title instead.
   */
  label?: string;
  /** Controlled open state */
  open?: boolean;
  /** When true, backdrop clicks do not close the drawer (default: false) */
  persistent?: boolean;
  /** Side from which the drawer slides in */
  placement?: DrawerPlacement;
  /**
   * When true (default), focus returns to the triggering element after the drawer closes.
   * Set to false to manage focus manually.
   */
  'return-focus'?: boolean;
  /** Drawer width/height preset */
  size?: DrawerSize;
  /**
   * Visible title text rendered inside the header.
   * Used as the dialog's accessible label via `aria-labelledby` when `label` is not set.
   */
  title?: string;
};

/**
 * A panel that slides in from any edge of the screen, built on the native `<dialog>` element.
 *
 * @element ore-drawer
 *
 * @attr {boolean} open - Controlled open state
 * @attr {boolean} default-open - Initial uncontrolled open state
 * @attr {string} placement - 'left' | 'right' (default) | 'top' | 'bottom'
 * @attr {string} size - 'sm' | 'lg' | 'full'
 * @attr {string} title - Visible header title text
 * @attr {string} label - Invisible aria-label (for drawers without a visible title)
 * @attr {boolean} dismissible - Show the close (×) button (default: true)
 * @attr {string} drag-handle-placement - 'outside' (default) | 'inset'
 * @attr {string} backdrop - Backdrop style: 'opaque' (default) | 'blur' | 'transparent'
 * @attr {boolean} persistent - Prevent backdrop-click from closing (default: false)
 *
 * @fires open-change - When the drawer state changes. detail: { open, reason }
 *
 * @slot header - Drawer header content
 * @slot - Main body content
 * @slot footer - Drawer footer content
 *
 * @cssprop --drawer-backdrop-bg - Backdrop background color
 * @cssprop --drawer-bg - Panel background color
 * @cssprop --drawer-size - Panel width (horizontal) or height (vertical)
 * @cssprop --drawer-shadow - Panel box-shadow
 * @cssprop --drawer-panel-blur - Panel backdrop blur amount
 *
 * @part drag-handle - Drawer drag handle.
 * @part panel - Panel container.
 * @part header - Header container.
 * @part close-btn - Close button.
 * @part body - Body content container.
 * @part footer - Footer container.
 * @example
 * ```html
 * <!-- With visible title -->
 * <ore-drawer open title="Settings" placement="right">
 *   <p>Settings content here.</p>
 * </ore-drawer>
 *
 * <!-- With custom header slot -->
 * <ore-drawer open placement="right">
 *   <span slot="header">Settings</span>
 *   <p>Settings content here.</p>
 * </ore-drawer>
 * ```
 */

export const DRAWER_TAG = 'ore-drawer' as const;
define<OreDrawerProps>(DRAWER_TAG, {
  props: {
    backdrop: prop.string<DrawerBackdrop>(),
    'default-open': prop.bool(false),
    dismissible: prop.bool(true),
    'drag-handle-placement': prop.oneOf(['outside', 'inset'] as const, 'outside'),
    'initial-focus': prop.string(),
    label: prop.string(),
    open: { default: undefined as boolean | undefined, parse: parseOptionalBool },
    persistent: prop.bool(false),
    placement: prop.oneOf(['left', 'right', 'top', 'bottom'] as const, 'right'),
    'return-focus': prop.bool(true),
    size: prop.string<DrawerSize>(),
    title: prop.string(),
  },
  setup(props) {
    const el = getHost();
    const emit = useEmit<OreDrawerEvents>();
    const slots = useSlots();

    const drawerLabelId = createStableId('drawer-label');
    const dialogRef = ref<HTMLDialogElement>();
    const panelRef = ref<HTMLDivElement>();

    // Drag-to-close state
    const isSwipeClosing = signal(false);
    let swipe: PanGesture | undefined;
    let swipeCloseTimer: ReturnType<typeof setTimeout> | undefined;

    const getHeaderText = () => props.label.value ?? props.title.value ?? '';
    const hasHeaderTitle = () => slots.has('header').value || !!getHeaderText();

    // Header is visible when there is slot content, a text label, or a close button.
    const hasHeader = () => hasHeaderTitle() || props.dismissible.value;
    const hasFooter = () => slots.has('footer').value;

    const getPlacement = (): DrawerPlacement => props.placement.value || 'right';

    const getSwipeConfig = (): DrawerSwipeConfig => drawerSwipeConfig[getPlacement()];

    const getSnapThreshold = (panel: HTMLElement, axis: PanAxis) => {
      const panelSize = axis === 'x' ? panel.offsetWidth : panel.offsetHeight;

      return Math.min(96, Math.max(36, panelSize * 0.18));
    };

    const shouldCommitSwipeClose = (distance: number, threshold: number) => {
      return getSwipeConfig().closingDistance(distance) >= threshold;
    };

    const finalizeSwipeClose = (panel: HTMLElement) => {
      if (!isSwipeClosing.value) return;

      if (swipeCloseTimer) {
        clearTimeout(swipeCloseTimer);
        swipeCloseTimer = undefined;
      }

      const dialog = dialogRef.value;

      if (!dialog) {
        isSwipeClosing.value = false;

        return;
      }

      dialog.style.transition = 'none';
      panel.style.opacity = '0';
      panel.style.visibility = 'hidden';
      void dialog.offsetWidth;

      requestClose('swipe');
    };

    const startSwipeClose = (panel: HTMLElement, swipe: DrawerSwipeConfig, committedDistance: number) => {
      if (isSwipeClosing.value) return;

      isSwipeClosing.value = true;

      const panelSize = swipe.axis === 'x' ? panel.offsetWidth : panel.offsetHeight;
      // Preserve overshoot so closing continues from the dragged position instead of snapping back.
      const exitDistance =
        committedDistance >= 0 ? Math.max(committedDistance, panelSize) : Math.min(committedDistance, -panelSize);
      const exitTransform = swipe.translate(exitDistance);

      // Commit the current drag position first so the magnetic snap continues
      // from the finger instead of jumping back to rest.
      panel.style.transition = 'transform 180ms cubic-bezier(0.2, 0.9, 0.2, 1), opacity 180ms ease-out';
      void panel.offsetWidth;

      const onExitTransitionEnd = (ev: TransitionEvent) => {
        if (ev.target !== panel || ev.propertyName !== 'transform') return;

        panel.removeEventListener('transitionend', onExitTransitionEnd);
        finalizeSwipeClose(panel);
      };

      panel.addEventListener('transitionend', onExitTransitionEnd);

      const durationMs = parseFloat(getComputedStyle(panel).transitionDuration) * 1000;

      swipeCloseTimer = setTimeout(() => {
        panel.removeEventListener('transitionend', onExitTransitionEnd);
        finalizeSwipeClose(panel);
      }, durationMs + 50);

      panel.style.transform = exitTransform;
      panel.style.opacity = '0.2';
    };

    const resetPanelDragStyles = (panel: HTMLElement) => {
      if (swipeCloseTimer) {
        clearTimeout(swipeCloseTimer);
        swipeCloseTimer = undefined;
      }

      // Re-enable transitions so the snap-back or exit animates.
      panel.style.transition = '';
      panel.style.transform = '';
      panel.style.opacity = '';
      panel.style.visibility = '';
      isSwipeClosing.value = false;
    };

    const handleSwipeEnd = ({ distance, reason }: PanGestureEndDetail): void => {
      const panel = panelRef.value;

      if (!panel) return;

      const threshold = getSnapThreshold(panel, getSwipeConfig().axis);

      if (reason === 'release' && shouldCommitSwipeClose(distance, threshold)) {
        startSwipeClose(panel, getSwipeConfig(), distance);

        return;
      }

      resetPanelDragStyles(panel);
    };

    // ────────────────────────────────────────────────────────────────
    // Overlay State Management
    // ────────────────────────────────────────────────────────────────

    const { closeWithAnimation, handleCancel, overlay, requestClose, watchOpenProp } = useDialogControl({
      beforeOpen: (dialog) => {
        // Clear any inline drag styles from a previous swipe-close so the CSS
        // entry animation starts from the correct base state.
        const panel = panelRef.value;

        if (panel) resetPanelDragStyles(panel);

        void dialog;
      },
      closeRequestDetail: (_reason) => ({ placement: props.placement.value ?? 'right' }),
      defaultOpen: props['default-open'],
      dialogRef,
      getPanelEl: () => panelRef.value,
      host: el,
      initialFocus: props['initial-focus'],
      isPersistent: () => Boolean(props.persistent.value),
      onCleanup,
      onEvent,
      onNativeClose: (reason) => {
        const panelEl = panelRef.value;

        // For swipe-close, keep the panel hidden and off-screen until the next
        // open cycle — resetting inline styles during native close can produce
        // a visible frame at the rest position on some browsers.
        if (panelEl && reason !== 'swipe') resetPanelDragStyles(panelEl);

        const dialog = dialogRef.value;

        if (dialog) dialog.style.transition = '';

        emit('open-change', { open: false, reason });
      },
      onOpen: (reason) => emit('open-change', { open: true, reason }),
      openProp: props.open,
      performClose: (dialog, reason) => {
        if (reason === 'swipe') {
          dialog.close();
        } else {
          closeWithAnimation();
        }
      },
      returnFocus: props['return-focus'],
    });

    const handleCloseButtonClick = () => {
      requestClose('trigger');
    };

    // ────────────────────────────────────────────────────────────────
    // Lifecycle: Setup Drawer Integration
    // ────────────────────────────────────────────────────────────────

    onMounted(() => {
      const dialog = dialogRef.value;

      if (!dialog) return;

      // Expose imperative API
      const drawerEl = el as DrawerElement;

      drawerEl.show = () => {
        overlay.open('programmatic');
      };

      drawerEl.hide = () => {
        overlay.close('programmatic');
      };

      // ────────────────────────────────────────────────────────────
      // Event Handlers: Cancel, Backdrop Click
      // ────────────────────────────────────────────────────────────
      // `handleCancel` (Escape via the native `cancel` event) is provided by
      // `useDialogControl` above — drawer only needs its own backdrop-click
      // handler since it has swipe-to-dismiss logic the shared dialog helper
      // doesn't know about.

      const handleBackdropClick = (e: MouseEvent) => {
        if (props.persistent.value) return;

        if (swipe?.active || isSwipeClosing.value) return;

        if (e.target !== dialog) return; // Click inside panel

        requestClose('outsideClick');
      };

      watchOpenProp();

      onEvent(dialog, 'cancel', handleCancel);
      onEvent(dialog, 'click', handleBackdropClick);

      // Drag-to-close handlers — scoped to the handle element only so interactions
      // with panel content don't accidentally start a drag.
      const panel = panelRef.value;
      const dragHandleEl = panel?.querySelector<HTMLElement>('[part="drag-handle"]');

      if (dragHandleEl) {
        swipe = createPanGesture(dragHandleEl, {
          axis: () => getSwipeConfig().axis,
          disabled: () => isSwipeClosing.value,
          onEnd: handleSwipeEnd,
          onMove: ({ distance }) => {
            const currentPanel = panelRef.value;

            if (!currentPanel) return;

            const swipeConfig = getSwipeConfig();
            const threshold = getSnapThreshold(currentPanel, swipeConfig.axis);
            const closingDistance = swipeConfig.closingDistance(distance);
            const progress = Math.min(closingDistance / threshold, 1);

            currentPanel.style.transition = 'none';
            currentPanel.style.transform = swipeConfig.translate(distance);
            currentPanel.style.opacity = String(1 - progress * 0.4);
          },
        });
      }

      return () => {
        swipe?.dispose();
        swipe = undefined;
        overlay.dispose();
      };
    });

    return html`
      <dialog
        ref=${dialogRef}
        aria-modal="true"
        aria-label="${() => props.label.value ?? null}"
        aria-labelledby="${() => (!props.label.value ? drawerLabelId : null)}">
        <div class="panel" part="panel" ref=${panelRef}>
          <div class="drag-handle" part="drag-handle" aria-label="Drag to close" role="button"></div>
          <div class="header" part="header" ?hidden=${() => !hasHeader()}>
            <span class="header-title" id="${drawerLabelId}" ?hidden=${() => !hasHeaderTitle()}>
              <slot name="header">${() => getHeaderText()}</slot>
            </span>
            <button
              class="close-btn"
              part="close-btn"
              type="button"
              aria-label="Close"
              ?hidden=${() => !props.dismissible.value}
              @click=${handleCloseButtonClick}>
              <ore-icon name="x" size="16" stroke-width="2.5" aria-hidden="true"></ore-icon>
            </button>
          </div>
          <div class="body" part="body">
            <slot></slot>
          </div>
          <div class="footer" part="footer" ?hidden=${() => !hasFooter()}>
            <slot name="footer"></slot>
          </div>
        </div>
      </dialog>
    `;
  },
  styles: [forcedColorsMixin, coarsePointerMixin, reducedMotionMixin, styles],
});
